import React, { useEffect, useMemo, useRef, useState } from 'react';
import { hasGoogleMapsKey, loadGoogleMaps } from './googleMapsLoader';
import { getLocationAddress, normalizeLocationValue } from './locationUtils';
import './LocationMapPreview.css';

const LocationMapPreview = ({ locations = [] }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRefs = useRef([]);
  const [mapReady, setMapReady] = useState(false);
  const normalizedLocations = useMemo(() => (
    (Array.isArray(locations) ? locations : [])
      .map(normalizeLocationValue)
      .filter((location) => location && getLocationAddress(location))
  ), [locations]);
  const firstLocation = normalizedLocations[0] || null;
  const hasCoordinates = normalizedLocations.some((location) => (
    Number.isFinite(Number(location.lat)) && Number.isFinite(Number(location.lng))
  ));

  useEffect(() => {
    if (!mapRef.current || !hasGoogleMapsKey() || !hasCoordinates) return undefined;
    let cancelled = false;

    loadGoogleMaps()
      .then((google) => {
        if (cancelled || !mapRef.current) return;
        const coordinateLocations = normalizedLocations
          .filter((location) => Number.isFinite(Number(location.lat)) && Number.isFinite(Number(location.lng)));
        if (coordinateLocations.length === 0) return;
        const center = {
          lat: Number(coordinateLocations[0].lat),
          lng: Number(coordinateLocations[0].lng),
        };
        const map = mapInstanceRef.current || new google.maps.Map(mapRef.current, {
          center,
          zoom: coordinateLocations.length > 1 ? 10 : 14,
          disableDefaultUI: true,
          zoomControl: true,
          clickableIcons: false,
          styles: [
            { elementType: 'geometry', stylers: [{ color: '#111827' }] },
            { elementType: 'labels.text.fill', stylers: [{ color: '#cbd5e1' }] },
            { elementType: 'labels.text.stroke', stylers: [{ color: '#0f172a' }] },
            { featureType: 'poi', stylers: [{ visibility: 'off' }] },
            { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#334155' }] },
            { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#0f2a3d' }] },
          ],
        });
        mapInstanceRef.current = map;
        markerRefs.current.forEach((marker) => marker.setMap(null));
        markerRefs.current = coordinateLocations.map((location) => new google.maps.Marker({
          map,
          position: { lat: Number(location.lat), lng: Number(location.lng) },
          title: getLocationAddress(location),
        }));
        if (coordinateLocations.length > 1) {
          const bounds = new google.maps.LatLngBounds();
          coordinateLocations.forEach((location) => bounds.extend({ lat: Number(location.lat), lng: Number(location.lng) }));
          map.fitBounds(bounds, 32);
        } else {
          map.setCenter(center);
          map.setZoom(14);
        }
        setMapReady(true);
      })
      .catch(() => setMapReady(false));

    return () => {
      cancelled = true;
    };
  }, [hasCoordinates, normalizedLocations]);

  if (normalizedLocations.length === 0) return null;

  return (
    <article className="card location-map-preview-card">
      <header className="card-header">
        <div>
          <h2>Kart</h2>
          <p className="card-subtitle">
            {normalizedLocations.length === 1 ? getLocationAddress(firstLocation) : `${normalizedLocations.length} registrerte lokasjoner`}
          </p>
        </div>
      </header>
      {hasGoogleMapsKey() && hasCoordinates ? (
        <div className={`location-map-preview-map ${mapReady ? 'is-ready' : ''}`} ref={mapRef} aria-label="Kartvisning" />
      ) : (
        <div className="location-map-preview-fallback">
          <strong>{getLocationAddress(firstLocation)}</strong>
          <span>
            {hasGoogleMapsKey()
              ? 'Velg en adresse fra Google-forslagene for å vise kart.'
              : 'Google Maps API-nøkkel mangler, så kartet vises som adresse enn så lenge.'}
          </span>
        </div>
      )}
    </article>
  );
};

export default LocationMapPreview;
